import { CloseOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import PageContent from '@zeus/core/layout/PageContent';
import VersionPreviewModal from '@zeus/core/modals/VersionPreviewModal';
import { readmeHistoryAdapter } from '_/common/adapters/readmeHistory.adapter';
import ReadmeHistory from '_/common/components/readme/ReadmeHistory';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import useFlowUrl from '_/features/flows/common/hooks/useFlowUrl';
import { useSaveFlowReadme } from '_/hooks/useFlows';
import React, { ReactElement, useState, useMemo, useEffect, useCallback } from 'react';

interface ReadmeVersion {
  after: {
    content: string;
  };
}

function FlowReadmeHistory(): ReactElement {
  const history = useNavigate();
  const { selectedFlow, flowReadmeVersions, projectId, flowSlug, flowId } = useFlowContext();
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProject, selectedProjectSlug } = useProject();
  const hasReadme = !!selectedFlow?.readme;
  const { mutate: saveReadme } = useSaveFlowReadme(projectId, flowSlug, hasReadme);
  const flowUrl = useFlowUrl();
  const { setActionButtons, setBreadcrumbs } = useTopHeader();

  // Adapt versions data to ReadmeHistory format
  const readmeVersions = useMemo(() => readmeHistoryAdapter(flowReadmeVersions?.items || []), [flowReadmeVersions]);

  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ReadmeVersion | Record<string, never>>({});

  const restoreToPreviousVersion = (version: ReadmeVersion) => {
    const content = version.after?.content || '';
    if (flowId) {
      saveReadme({ flowId: flowId, content: content });
    }

    // Navigate back to flow home after restore
    void history({ to: flowUrl });

    if (previewModalVisible) {
      setPreviewModalVisible(false);
      setSelectedVersion({});
    }
  };

  const previewPreviousVersion = (version?: ReadmeVersion) => {
    if (version) {
      setSelectedVersion(version);
    }
    setPreviewModalVisible(!previewModalVisible);
  };

  const handleReturnToFlow = useCallback(() => {
    void history({ to: flowUrl });
  }, [history, flowUrl]);

  const exitButton = useMemo(
    () => ({
      onClick: handleReturnToFlow,
      icon: <CloseOutlined />,
      tooltip: 'Return to Flow',
      label: 'Close',
    }),
    [handleReturnToFlow],
  );

  // Set up breadcrumbs and action buttons
  useEffect(() => {
    if (selectedFlow) {
      const breadcrumbs: BreadcrumbItem[] = [
        { title: 'Organizations', href: '/orgs' },
        { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
        { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
        {
          title: selectedProject?.name || '',
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
        },
        { title: 'Flows', href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows` },
        {
          title: selectedFlow.name,
          href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}`,
        },
        { title: 'Readme History' },
      ];

      setBreadcrumbs(breadcrumbs);
    }

    setActionButtons([exitButton]);

    return () => {
      setBreadcrumbs([]);
      setActionButtons([]);
    };
  }, [
    exitButton,
    setActionButtons,
    setBreadcrumbs,
    selectedFlow,
    selectedOrgSlug,
    selectedOrganization,
    selectedProject,
    selectedProjectSlug,
    flowSlug,
  ]);

  return (
    <>
      <PageContent>
        <ReadmeHistory
          readmeVersions={readmeVersions}
          onRestoreClick={restoreToPreviousVersion}
          onPreviewClick={previewPreviousVersion}
        />
      </PageContent>

      <VersionPreviewModal
        open={previewModalVisible}
        versionData={selectedVersion}
        onRestoreClick={restoreToPreviousVersion}
        onCancelClick={() => previewPreviousVersion()}
      />
    </>
  );
}

export default FlowReadmeHistory;
